import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskLine, Reveal } from "@/lib/motion";
import { BookingLink } from "@/components/BookingLink";
import { Emblem } from "@/components/Brand";
import { MeetMichael } from "@/components/MeetMichael";
import { Credentials } from "@/components/Credentials";
import { SITE, PRINCIPLES } from "@/data/site";

const BTN = "group inline-flex items-center gap-3 min-h-[48px] px-8 py-3.5 font-mono text-[13px] uppercase tracking-[0.14em] transition-colors";

const AboutHero = () => (
  <section data-testid="about-hero" className="bg-cream pt-40 pb-24 relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
      style={{ backgroundImage: "linear-gradient(#E2DACB 1px,transparent 1px)", backgroundSize: "100% 46px" }} />
    <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10">
      <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-8">About</p>
      <h1 className="font-serif font-medium tracking-tight leading-[1.03] text-navy text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
        <MaskLine delay={0.1}>Careful work and</MaskLine>
        <MaskLine delay={0.24}>clear communication,</MaskLine>
        <MaskLine delay={0.38}><span className="italic text-[#9a7a33]">from the founder.</span></MaskLine>
      </h1>
      <p className="anim-up mt-8 max-w-2xl text-ink-muted text-lg leading-relaxed" style={{ animationDelay: "0.55s" }}>
        The background, values, and training behind Oakford Ledger.
      </p>
    </div>
  </section>
);

const Mission = () => (
  <section data-testid="about-mission" className="bg-cream pb-28">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <Reveal>
        <div className="relative overflow-hidden border-t border-b border-[#D9CFB8] py-16 lg:py-20 grid lg:grid-cols-12 gap-10">
          {/* Faint oversized emblem as a quiet signature behind the mission */}
          <div aria-hidden="true" data-testid="mission-watermark"
            className="pointer-events-none select-none absolute -right-16 lg:-right-8 top-1/2 -translate-y-1/2 w-[340px] sm:w-[440px] lg:w-[560px] opacity-[0.06]">
            <Emblem sizes="560px" testId="mission-watermark-img" className="w-full h-auto" />
          </div>
          <div className="relative lg:col-span-3">
            <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f]">Our mission</p>
            <span aria-hidden="true" className="block mt-6 font-serif text-7xl leading-none text-gold/60">“</span>
          </div>
          <blockquote data-testid="mission-statement" className="relative lg:col-span-9 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.2] tracking-tight max-w-4xl">
            {SITE.mission}
          </blockquote>
        </div>
      </Reveal>
    </div>
  </section>
);

const Principles = () => (
  <section data-testid="about-principles-grid" className="bg-cream py-28 border-t border-[#D9CFB8]">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-5">What I value</p>
        <h2 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight max-w-xl leading-tight">The principles behind the work</h2>
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2DACB]">
        {PRINCIPLES.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="bg-cream-ivory p-10 h-full hover:bg-cream-card transition-colors duration-500">
              <span className="font-mono text-xs text-[#8a6b2f] tracking-[0.2em]">{p.num}</span>
              <h3 className="font-serif text-2xl lg:text-3xl text-navy mt-8">{p.title}</h3>
              <p className="text-ink-muted mt-4 leading-relaxed text-[15px]">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AboutCTA = () => (
  <section data-testid="about-cta-section" className="bg-navy text-parchment py-24">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
      <Reveal>
        <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-gold mb-5">Next step</p>
        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight max-w-xl">Ready to talk about your books?</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="flex flex-wrap gap-4">
          <BookingLink placement="about-cta" testId="about-consultation-btn" className={`${BTN} bg-gold text-navy hover:bg-gold-hover`}>
            Book a Consultation <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </BookingLink>
          <Link to="/contact" data-testid="about-inquiry-btn" className={`${BTN} text-parchment border border-parchment/30 hover:border-gold hover:text-gold`}>
            Send an Inquiry
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <AboutHero />
      <Mission />
      <MeetMichael />
      <Credentials />
      <Principles />
      <AboutCTA />
    </>
  );
}
